import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, AlertCircle, Check, Shield, Key, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function UpdateEmailPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/auth/update-email', { email });
      setEmailSent(true);
      toast.success('Verification email sent! Please check your inbox.');
      
      // Refresh user data
      await checkAuth();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update email';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    // Refresh auth to check if email was verified
    await checkAuth();
    navigate('/dashboard');
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await api.post('/api/auth/resend-verification');
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border border-border shadow-sm">
          <CardHeader className="text-center pb-3">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
              <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-xl font-semibold font-['Outfit']">
              Verification Email Sent
            </CardTitle>
            <CardDescription className="text-sm mt-2">
              We&apos;ve sent a verification link to <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Important:</strong> Click the link in the email to verify your address and unlock features like password reset and two-factor authentication.
              </p>
            </div>

            {/* Features unlocked after verification */}
            <div className="space-y-2 text-sm">
              <p className="font-medium text-muted-foreground">Features enabled after verification:</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Key className="h-3.5 w-3.5" />
                  <span className="text-xs">Password reset via email</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Shield className="h-3.5 w-3.5" />
                  <span className="text-xs">Two-factor authentication (2FA)</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="text-xs">Security notifications</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Button 
                onClick={handleContinue} 
                className="w-full"
              >
                Continue to Dashboard
              </Button>
              
              <Button 
                onClick={handleResendVerification}
                variant="outline"
                className="w-full"
                disabled={loading}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Resend Verification Email
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              You can verify your email later from profile settings, but some security features will be limited.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border border-border shadow-sm">
        <CardHeader className="text-center pb-3">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl font-semibold font-['Outfit']">
            Add Your Email Address
          </CardTitle>
          <CardDescription className="text-sm mt-2">
            Secure your account and enable important features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-700 dark:text-amber-300">
                <p className="font-medium mb-1">Email Required</p>
                <p>Your account needs a verified email address to enable password recovery and enhanced security features.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                We&apos;ll send a verification link to this address
              </p>
            </div>

            {/* What you'll unlock */}
            <div className="p-3 rounded-md bg-muted/30 border border-border">
              <p className="text-xs font-medium mb-2">You&apos;ll unlock:</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Key className="h-3 w-3" />
                  <span>Password reset capability</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield className="h-3 w-3" />
                  <span>Two-factor authentication</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  <span>Security alerts & notifications</span>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Verification Email'}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By adding your email, you agree to receive account-related communications
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
